import * as THREE from "three";
import { LOWPOLY_GLOW_ATTRIBUTE, patchLowPolyShader, type LowPolyUniforms } from "./lowpoly";

// The low-poly look for places (spike 002 C4): the building kit's surface and the water's.
//
// The kit surface is the low-poly surface (flat shading, palette vertex colours, glow, haze) plus two per-vertex
// numbers meshkit's kit writes:
//   lit  how much a face glows at night (windows, lamps): it shows `uKitLitColour` at the night factor the game sets
//        (`setKitNight`, from its time of day), through less haze, like the glow;
//   cut  owner × 8 + group: the faces of the groups in `uKitCutMask` that belong to `uKitCutOwner` dissolve with a
//        4×4 ordered dither as `uKitCutFade` goes 0 → 1, so a building's roof and near walls fade away from a camera
//        inside it. No transparency, no sorting; one program for every building.
// The water surface is a Lambert sheet faceted by its own waves: three seeded sines displace it, damped to nothing at
// the shore (meshkit's `shore` attribute), and a foam rim pulses along the shore. Colour (the depth tint) is baked into
// the sheet's vertices. Cheap: one program, one draw per sheet, no reflection, no depth texture.

/** The kit surface's uniforms (one set per scene). */
export interface KitUniforms {
  uKitNight: THREE.IUniform<number>;
  uKitLitColour: THREE.IUniform<THREE.Color>;
  uKitCutOwner: THREE.IUniform<number>;
  uKitCutMask: THREE.IUniform<number>;
  uKitCutFade: THREE.IUniform<number>;
}

/** Fresh kit uniforms: day, nothing cut. */
export function kitUniforms(litColour = "#ffc979"): KitUniforms {
  return {
    uKitNight: { value: 0 },
    uKitLitColour: { value: new THREE.Color(litColour) },
    uKitCutOwner: { value: -1 },
    uKitCutMask: { value: 0 },
    uKitCutFade: { value: 0 },
  };
}

/** How much the lit faces glow (0 day .. 1 night). */
export function setKitNight(k: KitUniforms, night: number): void {
  k.uKitNight.value = Math.min(1, Math.max(0, night));
}

/** Cut away `mask`'s groups of `owner`'s faces, `fade` of the way (0 none, 1 gone); owner −1 cuts nothing. */
export function setKitCut(k: KitUniforms, owner: number, mask: number, fade: number): void {
  k.uKitCutOwner.value = owner;
  k.uKitCutMask.value = mask;
  k.uKitCutFade.value = Math.min(1, Math.max(0, fade));
}

const VERT_DECL = /* glsl */ `attribute float lit;\nattribute float cut;\nuniform float uKitNight;\nvarying float vKitLit;\nvarying float vKitCut;\n`;
const FRAG_DECL = /* glsl */ `uniform vec3 uKitLitColour;\nuniform float uKitCutOwner;\nuniform float uKitCutMask;\nuniform float uKitCutFade;\nvarying float vKitLit;\nvarying float vKitCut;\n`;
/** The ordered-dither cut test (after the clipping planes). */
const CUT_GLSL = /* glsl */ `
  {
    float kOwner = floor(vKitCut / 8.0 + 0.01);
    float kGroup = floor(vKitCut - kOwner * 8.0 + 0.5);
    if (kGroup > 0.5 && abs(kOwner - uKitCutOwner) < 0.5 && mod(floor(uKitCutMask / exp2(kGroup) + 0.001), 2.0) > 0.5) {
      vec2 q = mod(floor(gl_FragCoord.xy), 4.0);
      // a 4×4 Bayer matrix, as arithmetic
      float b = mod(q.x * 2.0 + q.y * 3.0, 4.0) * 4.0 + mod(q.x + q.y * 2.0, 4.0);
      if (uKitCutFade * 16.0 > b + 0.5) discard;
    }
  }
`;

/** The shader text the kit patch adds and where (exported for tests). */
export const KIT_CHUNKS = {
  glowLine: `vLpGlow = ${LOWPOLY_GLOW_ATTRIBUTE};`,
  vert: `vLpGlow = ${LOWPOLY_GLOW_ATTRIBUTE};\n    vKitLit = lit * uKitNight;\n    vKitCut = cut;\n    vLpGlow = max(vLpGlow, vKitLit);`,
  // the low-poly patch's glow mix (its `lpG`: the glow, or the night lamps for faces with glow 2)
  glowMix: "outgoingLight = mix(outgoingLight, diffuseColor.rgb * uLpGlow, lpG);",
  litMix: "outgoingLight = mix(outgoingLight, diffuseColor.rgb * uLpGlow, lpG);\n  outgoingLight = mix(outgoingLight, uKitLitColour * uLpGlow, vKitLit);",
  clip: "#include <clipping_planes_fragment>",
  cut: `#include <clipping_planes_fragment>\n${CUT_GLSL}`,
};

/** Patch a shader object with the low-poly surface and then the kit's lit and cut. Throws if a hook is missing. */
export function patchKitShader(shader: { vertexShader: string; fragmentShader: string; uniforms: Record<string, THREE.IUniform> }, u: LowPolyUniforms, k: KitUniforms): void {
  patchLowPolyShader(shader, u);
  Object.assign(shader.uniforms, k);
  const need = (src: string, chunk: string, where: string): void => {
    if (!src.includes(chunk)) throw new Error(`@rpg/look: kit patch: ${where} has no ${chunk}`);
  };
  need(shader.vertexShader, KIT_CHUNKS.glowLine, "vertex shader");
  need(shader.fragmentShader, KIT_CHUNKS.glowMix, "fragment shader");
  need(shader.fragmentShader, KIT_CHUNKS.clip, "fragment shader");
  shader.vertexShader = VERT_DECL + shader.vertexShader.replace(KIT_CHUNKS.glowLine, KIT_CHUNKS.vert);
  shader.fragmentShader = FRAG_DECL + shader.fragmentShader.replace(KIT_CHUNKS.glowMix, KIT_CHUNKS.litMix).replace(KIT_CHUNKS.clip, KIT_CHUNKS.cut);
}

/**
 * The kit's one surface material: the low-poly surface with night-lit faces and the dithered cut-away. Every geometry
 * on it needs glow, lit and cut attributes (meshkit's kit writes them). Share it: one program for every building.
 */
export function kitSurface(u: LowPolyUniforms, k: KitUniforms): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, side: THREE.DoubleSide });
  m.name = "lowpoly-kit";
  m.onBeforeCompile = (s) => patchKitShader(s, u, k);
  m.customProgramCacheKey = () => "lowpoly-kit-1";
  return m;
}

// ── water ──────────────────────────────────────────────────────────────────────────────────────────

/** The water's look (the game's numbers). */
export interface LowPolyWaterLook {
  /** Wave height (m), wavelength (m) and speed (m/s) of the main swell; two smaller ones ride on it. */
  height: number;
  length: number;
  speed: number;
  /** The waves reach full height this far inside the shore (m). */
  calmShore: number;
  /** Foam: its colour, its width inside the shore (m), its pulse rate (1/s). */
  foam: string;
  foamWidth: number;
  foamPulse: number;
  /** Directions of the three wave trains (rad, about y), from the game's seed. */
  directions: readonly [number, number, number];
}

/** The water material's uniforms. */
export interface WaterUniforms {
  uWaterTime: THREE.IUniform<number>;
  uWaterWave: THREE.IUniform<THREE.Vector4>;
  uWaterDirs: THREE.IUniform<THREE.Vector3>;
  uWaterFoam: THREE.IUniform<THREE.Color>;
  uWaterFoamShape: THREE.IUniform<THREE.Vector2>;
}

/** Uniforms for a water look. */
export function waterUniforms(w: LowPolyWaterLook): WaterUniforms {
  return {
    uWaterTime: { value: 0 },
    uWaterWave: { value: new THREE.Vector4(w.height, (2 * Math.PI) / Math.max(0.01, w.length), w.speed, Math.max(0.01, w.calmShore)) },
    uWaterDirs: { value: new THREE.Vector3(...w.directions) },
    uWaterFoam: { value: new THREE.Color(w.foam) },
    uWaterFoamShape: { value: new THREE.Vector2(w.foamWidth, w.foamPulse) },
  };
}

/** The wave height the vertex shader adds at (x, z) and time t, shore distance s (for tests and CPU floaters). */
export function waveAt(w: LowPolyWaterLook, x: number, z: number, t: number, s: number): number {
  const k = (2 * Math.PI) / Math.max(0.01, w.length);
  const damp = Math.min(1, Math.max(0, s / Math.max(0.01, w.calmShore)));
  const train = (dir: number, kk: number, a: number, sp: number): number => a * Math.sin((x * Math.cos(dir) + z * Math.sin(dir)) * kk + t * sp * kk);
  return damp * (train(w.directions[0], k, w.height, w.speed) + train(w.directions[1], k * 1.7, w.height * 0.45, w.speed * 1.3) + train(w.directions[2], k * 2.9, w.height * 0.2, w.speed * 1.7));
}

const WATER_VERT_DECL = /* glsl */ `attribute float shore;\nuniform float uWaterTime;\nuniform vec4 uWaterWave;\nuniform vec3 uWaterDirs;\nvarying float vWaterShore;\n
float waterTrain(vec2 p, float dir, float k, float a, float sp) { return a * sin(dot(p, vec2(cos(dir), sin(dir))) * k + uWaterTime * sp * k); }
`;
const WATER_VERT = /* glsl */ `#include <begin_vertex>
  {
    vec2 wp = (modelMatrix * vec4(transformed, 1.0)).xz;
    float damp = clamp(shore / uWaterWave.w, 0.0, 1.0);
    float h = waterTrain(wp, uWaterDirs.x, uWaterWave.y, uWaterWave.x, uWaterWave.z)
            + waterTrain(wp, uWaterDirs.y, uWaterWave.y * 1.7, uWaterWave.x * 0.45, uWaterWave.z * 1.3)
            + waterTrain(wp, uWaterDirs.z, uWaterWave.y * 2.9, uWaterWave.x * 0.2, uWaterWave.z * 1.7);
    transformed.y += h * damp;
    vWaterShore = shore;
  }`;
const WATER_FRAG_DECL = /* glsl */ `uniform float uWaterTime;\nuniform vec3 uWaterFoam;\nuniform vec2 uWaterFoamShape;\nvarying float vWaterShore;\n`;
const WATER_FRAG = /* glsl */ `#include <color_fragment>
  {
    float pulse = 0.75 + 0.25 * sin(uWaterTime * uWaterFoamShape.y + vWaterShore * 5.0);
    float f = 1.0 - smoothstep(0.0, uWaterFoamShape.x * pulse, vWaterShore);
    diffuseColor.rgb = mix(diffuseColor.rgb, uWaterFoam, f * 0.85);
  }`;

/** Patch a shader object with the low-poly surface and the water's waves and foam. */
export function patchWaterShader(shader: { vertexShader: string; fragmentShader: string; uniforms: Record<string, THREE.IUniform> }, u: LowPolyUniforms, w: WaterUniforms): void {
  patchLowPolyShader(shader, u);
  Object.assign(shader.uniforms, w);
  if (!shader.vertexShader.includes("#include <begin_vertex>")) throw new Error("@rpg/look: water patch: vertex shader has no #include <begin_vertex>");
  if (!shader.fragmentShader.includes("#include <color_fragment>")) throw new Error("@rpg/look: water patch: fragment shader has no #include <color_fragment>");
  shader.vertexShader = WATER_VERT_DECL + shader.vertexShader.replace("#include <begin_vertex>", WATER_VERT);
  shader.fragmentShader = WATER_FRAG_DECL + shader.fragmentShader.replace("#include <color_fragment>", WATER_FRAG);
}

/** The water material: Lambert, flat-shaded (its waves facet), vertex-coloured, with the low-poly haze. Advance
 *  `uWaterTime` each frame (real seconds). Geometries need glow and shore (meshkit's `waterSurface`). */
export function lowPolyWater(u: LowPolyUniforms, w: WaterUniforms): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
  m.name = "lowpoly-water";
  m.onBeforeCompile = (s) => patchWaterShader(s, u, w);
  m.customProgramCacheKey = () => "lowpoly-water-1";
  return m;
}
